import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { ExternalLink } from 'lucide-react';
import { Dialog } from '@/components/ui/Dialog';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { api } from '@/lib/api';
import { formatDateTime, formatRelative } from '@/lib/utils';
import type { LeadDetail, LeadItem } from '@/types/api';
import { getStateTone } from './LeadsPage';

interface LeadDetailDrawerProps {
  lead: LeadItem | null;
  onClose: () => void;
}

export function LeadDetailDrawer({ lead, onClose }: LeadDetailDrawerProps) {
  const [qualifyIntent, setQualifyIntent] = useState<'positive' | 'negative' | 'meeting_booked' | null>(null);
  const [note, setNote] = useState('');
  const qc = useQueryClient();

  const { data, isLoading } = useQuery<LeadDetail>({
    queryKey: ['leads', lead?.state_id],
    queryFn: () => api.get<LeadDetail>(`/v1/leads/${lead!.state_id}`),
    enabled: !!lead?.state_id,
  });

  const qualify = useMutation({
    mutationFn: ({ intent, note }: { intent: string; note?: string }) =>
      api.post(`/v1/leads/${lead!.state_id}/qualify-manual`, { intent, note }),
    onSuccess: () => {
      toast.success('Lead qualifié');
      qc.invalidateQueries({ queryKey: ['leads'] });
      setQualifyIntent(null);
      setNote('');
      onClose();
    },
    onError: () => toast.error('Échec de la qualification'),
  });

  if (!lead) return null;

  const name = [data?.first_name, data?.last_name].filter(Boolean).join(' ') || '—';

  return (
    <Dialog
      open={!!lead}
      onClose={onClose}
      title={name}
      description={data?.company_name ?? undefined}
      size="lg"
      footer={
        qualifyIntent ? (
          <div className="flex items-center gap-2 w-full">
            <input
              type="text"
              className="input flex-1 text-sm"
              placeholder="Note optionnelle…"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
            <Button
              size="sm"
              onClick={() => qualify.mutate({ intent: qualifyIntent, note: note || undefined })}
              disabled={qualify.isPending}
            >
              Confirmer
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setQualifyIntent(null)}>
              Annuler
            </Button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={() => setQualifyIntent('positive')}>
              ✓ Qualifié
            </Button>
            <Button size="sm" variant="secondary" onClick={() => setQualifyIntent('meeting_booked')}>
              📅 RDV pris
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setQualifyIntent('negative')}>
              ✗ Pas intéressé
            </Button>
          </div>
        )
      }
    >
      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-4 w-full" />
          ))}
        </div>
      ) : data ? (
        <div className="space-y-5 text-sm">
          {/* Contact info */}
          <section>
            <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Contact</h4>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-1">
              <dt className="text-gray-500">Email</dt>
              <dd>{data.email ?? '—'}</dd>
              <dt className="text-gray-500">Téléphone</dt>
              <dd>{data.phone ?? '—'}</dd>
              <dt className="text-gray-500">Poste</dt>
              <dd>{data.position ?? '—'}</dd>
              <dt className="text-gray-500">Société</dt>
              <dd>{data.company_name ?? '—'}</dd>
              <dt className="text-gray-500">Domaine</dt>
              <dd>{data.company_domain ?? '—'}</dd>
              <dt className="text-gray-500">Industrie</dt>
              <dd>{data.industry ?? '—'}</dd>
              {data.hubspot_contact_id && (
                <>
                  <dt className="text-gray-500">HubSpot</dt>
                  <dd>
                    <a
                      href={`https://app.hubspot.com/contacts/0/contact/${data.hubspot_contact_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-brand-primary hover:underline"
                    >
                      {data.hubspot_contact_id}
                      <ExternalLink size={10} />
                    </a>
                  </dd>
                </>
              )}
            </dl>
          </section>

          {/* State */}
          <section>
            <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">État</h4>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-1">
              <dt className="text-gray-500">Courant</dt>
              <dd>
                <Badge tone={getStateTone(data.state)}>{data.state}</Badge>
              </dd>
              <dt className="text-gray-500">Campagne</dt>
              <dd>{data.campaign_name ?? '—'}</dd>
              <dt className="text-gray-500">Mis à jour</dt>
              <dd>{formatDateTime(data.updated_at)}</dd>
              <dt className="text-gray-500">Créé</dt>
              <dd>{formatDateTime(data.created_at)}</dd>
            </dl>
          </section>

          {/* Mail attempts */}
          {data.mail_attempts.length > 0 && (
            <section>
              <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                Tentatives mail ({data.mail_attempts.length})
              </h4>
              <ul className="space-y-1">
                {data.mail_attempts.map((m) => (
                  <li key={m.id} className="flex items-center justify-between text-xs p-2 rounded bg-gray-50">
                    <span className="text-gray-500">{m.provider}</span>
                    <Badge tone={m.status === 'replied' ? 'green' : m.status === 'bounced' ? 'red' : 'gray'}>
                      {m.status}
                    </Badge>
                    <span className="text-gray-400">{formatRelative(m.sent_at)}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Form fills */}
          {data.form_fills.length > 0 && (
            <section>
              <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                Form-fills ({data.form_fills.length})
              </h4>
              <ul className="space-y-1">
                {data.form_fills.map((f) => (
                  <li key={f.id} className="flex items-center justify-between text-xs p-2 rounded bg-gray-50">
                    <span className="truncate max-w-[200px] text-gray-500">{f.form_url ?? '—'}</span>
                    <Badge tone={f.status === 'done' ? 'green' : f.status === 'failed' ? 'red' : 'yellow'}>
                      {f.status}
                    </Badge>
                    <span className="text-gray-400">{formatRelative(f.finished_at)}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Transitions */}
          {data.transitions.length > 0 && (
            <section>
              <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                Historique ({data.transitions.length})
              </h4>
              <ul className="space-y-1 max-h-48 overflow-y-auto">
                {data.transitions.map((t) => (
                  <li key={t.id} className="flex items-center gap-2 text-xs text-gray-500">
                    <span className="text-gray-400 shrink-0">{formatRelative(t.ts)}</span>
                    <span className="font-mono text-gray-400">{t.from_state}</span>
                    <span>→</span>
                    <span className="font-mono text-brand-dark">{t.to_state}</span>
                    {t.trigger && <span className="text-gray-300">({t.trigger})</span>}
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      ) : null}
    </Dialog>
  );
}
